import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { change } from 'redux-form';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import { Grid, Field, Flex, Icon, Popup } from 'nallian-shared-ui/lib/components';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
  CheckBoxFieldValueComponent,
  TextAreaFieldValueComponent,
  PhoneFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { languagesService } from 'nallian-shared-ui/lib/services';
import apiRoutes from '../../../../../app/api/routes';
import { PRINCIPAL_FORM_NAME, PRINCIPAL_STATES } from '../../../constants';
import ReadOnlyFieldValueComponent from '../../../../app/components/ReadOnlyFieldValueComponent';
import PrincipalAutoCompleteValueComponent from '../../../../app/components/PrincipalAutoCompleteValueComponent';
import { NoBreakSpan } from '../../../../app/styles';
import OrganisationCommunitiesService from '../../../services/organisationCommunitiesService';

const PrincipalDetailsTab = ({
  readOnly,
  timezonesOverviewFetch,
  languagesFetch,
  data,
  editMode,
  prefillLocalFields,
  hideInviteFields,
  showPrincipalIdField,
  organisationId,
  dispatchOrganisationCommunitiesGet,
  organisationCommunitiesFetch,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const timezoneOptions = useMemo(
    () => (timezonesOverviewFetch.value || []).map(v => ({ key: v, value: v, text: v })),
    [timezonesOverviewFetch.value]
  );

  const languageOptions = useMemo(
    () =>
      sortBy(languagesFetch.value || [], 'name').map(l => ({
        key: l.id,
        value: l.id,
        text: l.name,
      })),
    [languagesFetch.value]
  );

  const communityOptions = useMemo(
    () =>
      (organisationCommunitiesFetch.value || []).map(c => ({
        key: c.id,
        value: c.id,
        text: c.name,
        organisationId,
        url: c.url,
      })),
    [organisationCommunitiesFetch.value, organisationId]
  );

  const sendInvitation = useSelector(state =>
    get(state, `form.${PRINCIPAL_FORM_NAME}.values.inviteRequest.sendInvitation`)
  );

  useEffect(() => {
    if (organisationId) dispatchOrganisationCommunitiesGet(organisationId);
  }, [dispatchOrganisationCommunitiesGet, organisationId]);

  useEffect(() => {
    if (prefillLocalFields) {
      // if they change we need to check for default browser values to check
      const browserLanguage = (navigator.language || navigator.userLanguage).slice(0, 2);
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (timezoneOptions.find(tz => tz.value === browserTimezone)) {
        dispatch(change(PRINCIPAL_FORM_NAME, `timeZone`, browserTimezone));
      }

      if (languageOptions.find(l => l.value === browserLanguage)) {
        dispatch(change(PRINCIPAL_FORM_NAME, `languageId`, browserLanguage));
      }
    }
  }, [languageOptions, timezoneOptions, dispatch, prefillLocalFields]);

  // set community id in inviteRequest if there is only one community option (which won't be visible)
  useEffect(() => {
    if (!editMode && sendInvitation && communityOptions?.length) {
      if (communityOptions.length === 1) {
        const community = communityOptions[0];
        if (community?.organisationId === organisationId) {
          dispatch(change(PRINCIPAL_FORM_NAME, 'inviteRequest.communityId', community.value));
        }
      } else {
        // if there are multiple communities, match the community with the URL
        const matchingCommunity = communityOptions.find(c => c.url === window.location.origin);
        if (matchingCommunity) {
          dispatch(
            change(PRINCIPAL_FORM_NAME, 'inviteRequest.communityId', matchingCommunity.value)
          );
        }
      }
    }
  }, [dispatch, organisationId, communityOptions, sendInvitation, editMode]);

  return (
    <Grid margin="1rem -1rem 0 -1rem">
      {showPrincipalIdField && organisationId && (
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            name="principalId"
            organisationId={organisationId}
            valueComponent={PrincipalAutoCompleteValueComponent}
          />
        </Grid.Column>
      )}
      <Grid.Column mobile={16} tablet={16} computer={16}>
        <Field
          fluid
          label={t('fullName')}
          name="fullName"
          valueComponent={readOnly ? ReadOnlyFieldValueComponent : InputFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          label={t('emailAddress')}
          type="email"
          name="primaryEmailAddress"
          valueComponent={readOnly ? ReadOnlyFieldValueComponent : InputFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          label={t('phoneNumber')}
          type="text"
          name="primaryPhoneNumber"
          valueComponent={readOnly ? ReadOnlyFieldValueComponent : PhoneFieldValueComponent}
        />
        <Flex margin="0 0 -1.5rem">
          <Field
            fluid
            toggle
            label={
              <Popup
                position="top left"
                on="hover"
                inverted
                hoverable
                trigger={
                  <NoBreakSpan>
                    <Flex alignItems="center">
                      {t('preApproveForLinking')}
                      <Icon name="question circle" margin="-0.2rem 0 0 0.5rem" />
                    </Flex>
                  </NoBreakSpan>
                }
              >
                {t('preApproveForLinkingInfo')}
              </Popup>
            }
            name="preApproveForLinking"
            valueComponent={CheckBoxFieldValueComponent}
          />
        </Flex>
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          clearable
          search
          label={t('language')}
          name="languageId"
          options={languageOptions}
          valueComponent={readOnly ? ReadOnlyFieldValueComponent : SelectFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          search
          clearable
          label={t('timezone')}
          name="timeZone"
          options={timezoneOptions}
          valueComponent={readOnly ? ReadOnlyFieldValueComponent : SelectFieldValueComponent}
        />
      </Grid.Column>
      {(!editMode || (!readOnly && editMode && data?.activeState === PRINCIPAL_STATES.PENDING)) && (
        <Grid.Column mobile={16} tablet={16} computer={16}>
          {!hideInviteFields && (
            <Field
              fluid
              toggle
              label={t('sendInvitation')}
              name="inviteRequest.sendInvitation"
              valueComponent={CheckBoxFieldValueComponent}
            />
          )}
          {sendInvitation && (
            <>
              {communityOptions?.length > 1 && (
                <Field
                  fluid
                  search
                  clearable
                  label={t('selectCommunityForInvitation')}
                  type="text"
                  name="inviteRequest.communityId"
                  options={communityOptions}
                  valueComponent={SelectFieldValueComponent}
                />
              )}
              <Field
                fluid
                label={t('inviteFreeText')}
                type="text"
                name="inviteRequest.freeText"
                valueComponent={TextAreaFieldValueComponent}
              />
            </>
          )}
        </Grid.Column>
      )}
    </Grid>
  );
};

PrincipalDetailsTab.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  timezonesOverviewFetch: PropTypes.object.isRequired,
  languagesFetch: PropTypes.object.isRequired,
  showPrincipalIdField: PropTypes.bool.isRequired,
  dispatchOrganisationCommunitiesGet: PropTypes.func.isRequired,
  organisationCommunitiesFetch: PropTypes.object.isRequired,
  organisationId: PropTypes.string,
  prefillLocalFields: PropTypes.bool,
  hideInviteFields: PropTypes.bool,
  editMode: PropTypes.bool,
  data: PropTypes.object,
};

PrincipalDetailsTab.defaultProps = {
  data: undefined,
  editMode: false,
  hideInviteFields: false,
  prefillLocalFields: false,
  organisationId: undefined,
};

export default compose(
  lookupService({
    resource: 'timezonesOverview',
    url: apiRoutes.getTimezones(),
  }),
  languagesService,
  OrganisationCommunitiesService
)(PrincipalDetailsTab);
